import { queryOptions, type QueryClient } from '@mint-lib/query-client';
import { KnowledgeBase } from '@mint-sdk/api-ai-assistant';

import {
  ROOT_FOLDER_ID,
  toApiFolderId,
  USERS_PRIVATE_FOLDERS_ID,
} from '../types/fileSystem.js';
import { REFETCH_PROCESSED_FILES } from './rag.js';

const LISTING_FRESH_MS = 60_000;

const hasPendingFiles = (listing?: KnowledgeBase.KnowledgeBaseListing) =>
  listing?.files?.some((file) => file.status === 'pending') ?? false;

export const toListedFolderId = (folderId: string) =>
  folderId === USERS_PRIVATE_FOLDERS_ID ? ROOT_FOLDER_ID : folderId;

const isRealFolder = (folderId: string) =>
  folderId !== ROOT_FOLDER_ID && folderId !== USERS_PRIVATE_FOLDERS_ID;

export const getKnowledgeBaseListingOption = (folderId: string) =>
  queryOptions({
    ...KnowledgeBase.getKnowledgeBaseListing({
      params: { folderId: toApiFolderId(folderId) },
    }),
    staleTime: LISTING_FRESH_MS,
    refetchInterval: (query) =>
      hasPendingFiles(query.state.data) ? REFETCH_PROCESSED_FILES : false,
  });

export const getFolderOption = (folderId: string) =>
  queryOptions({
    ...KnowledgeBase.getFolder({ urlParams: { folderId } }),
    staleTime: LISTING_FRESH_MS,
  });

// Loads folder for display; `fresh` skips the cached listing.
export const loadFolder = async (
  queryClient: QueryClient,
  folderId: string,
  { fresh = false } = {},
) => {
  const listing = getKnowledgeBaseListingOption(toListedFolderId(folderId));

  const loadListing = fresh
    ? queryClient.fetchQuery({ ...listing, staleTime: 0 })
    : queryClient.ensureQueryData({ ...listing, revalidateIfStale: true });

  const loadDetails = isRealFolder(folderId)
    ? queryClient.ensureQueryData({
        ...getFolderOption(folderId),
        revalidateIfStale: true,
      })
    : undefined;

  await Promise.allSettled([loadListing, loadDetails]);
};

type ListingUpdate = (
  folders: KnowledgeBase.Folder[],
) => KnowledgeBase.Folder[];

const getListingKey = (folderId: string) =>
  getKnowledgeBaseListingOption(toListedFolderId(folderId)).queryKey;

export const updateListedFolders = (
  queryClient: QueryClient,
  folderId: string,
  update: ListingUpdate,
  { cancelRefetch = false } = {},
) => {
  const queryKey = getListingKey(folderId);

  if (cancelRefetch) void queryClient.cancelQueries({ queryKey });

  queryClient.setQueryData(
    queryKey,
    (listing) =>
      listing && { ...listing, folders: update(listing.folders ?? []) },
  );
};

export const moveListedNodes = (
  queryClient: QueryClient,
  { from, to }: { from: string; to: string },
  nodeIds: ReadonlySet<string>,
) => {
  const fromKey = getListingKey(from);
  const toKey = getListingKey(to);
  const previousFrom = queryClient.getQueryData(fromKey);
  const previousTo = queryClient.getQueryData(toKey);

  void queryClient.cancelQueries({ queryKey: fromKey });
  void queryClient.cancelQueries({ queryKey: toKey });

  const movedFolders = (previousFrom?.folders ?? [])
    .filter(({ folderId }) => nodeIds.has(folderId))
    .map((folder) => ({ ...folder, parentFolderId: toApiFolderId(to) }));
  const movedFiles = (previousFrom?.files ?? [])
    .filter(({ fileId }) => nodeIds.has(fileId))
    .map((file) => ({ ...file, folderId: toApiFolderId(to) }));

  queryClient.setQueryData(
    fromKey,
    (listing) =>
      listing && {
        ...listing,
        folders: (listing.folders ?? []).filter(
          ({ folderId }) => !nodeIds.has(folderId),
        ),
        files: (listing.files ?? []).filter(
          ({ fileId }) => !nodeIds.has(fileId),
        ),
      },
  );

  queryClient.setQueryData(
    toKey,
    (listing) =>
      listing && {
        ...listing,
        folders: [...(listing.folders ?? []), ...movedFolders],
        files: [...(listing.files ?? []), ...movedFiles],
      },
  );

  return () => {
    queryClient.setQueryData(fromKey, previousFrom);
    queryClient.setQueryData(toKey, previousTo);
  };
};

export const forgetListings = (queryClient: QueryClient, folderIds: string[]) =>
  folderIds.forEach((folderId) => {
    void queryClient.cancelQueries({ queryKey: getListingKey(folderId) });
  });
